import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState } from "../../../../../app/rootReducer";
import { fetchCompaniesAction, setError } from "../slices/companiesSlice";
import { FetchCompaniesRequest } from "../../../../../api/fetchCompanies/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";

interface CompanyFilters {
  companyName?: string;
  intacctId?: string;
  salesforceId?: string;
  stage?: string;
}

// Helper to read initial state from URL
function getInitialStateFromUrl(searchParams: URLSearchParams) {
  const page = searchParams.get("page");
  const pageSize = searchParams.get("pageSize");
  const sortBy = searchParams.get("sortBy");
  const sortOrder = searchParams.get("sortOrder");

  return {
    pagination: {
      pageIndex: page ? parseInt(page, 10) - 1 : 0,
      pageSize: pageSize ? parseInt(pageSize, 10) : 10,
    },
    sorting: sortBy
      ? [{ id: sortBy, desc: sortOrder === "DESC" }]
      : ([] as SortingState),
    filters: {
      companyName: searchParams.get("companyName") || undefined,
      intacctId: searchParams.get("intacctId") || undefined,
      salesforceId: searchParams.get("salesforceId") || undefined,
      stage: searchParams.get("stage") || undefined,
    },
  };
}

export const useCompanies = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const companies = useSelector(
    (state: RootState) => state.companies.companies,
  );
  const totalCount = useSelector(
    (state: RootState) => state.companies.totalCount,
  );
  const loading = useSelector((state: RootState) => state.companies.loading);
  const error = useSelector((state: RootState) => state.companies.error);

  // Initialize state from URL params (only on mount)
  const initialState = React.useMemo(
    () => getInitialStateFromUrl(searchParams),
    [],
  );

  const [pagination, setPagination] = useState<PaginationState>(
    initialState.pagination,
  );

  const [sorting, setSorting] = useState<SortingState>(initialState.sorting);

  const [filters, setFilters] = useState<CompanyFilters>(initialState.filters);
  const [columnVisibility] = useState<{
    [key: string]: boolean;
  }>({});

  const requestData = React.useMemo<FetchCompaniesRequest>(() => {
    return {
      companyName: filters.companyName,
      intacctId: filters.intacctId,
      salesforceId: filters.salesforceId,
      stage: filters.stage,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sorting.length > 0 ? sorting[0].id : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : undefined,
    };
  }, [pagination, sorting, filters]);

  useEffect(() => {
    dispatch(fetchCompaniesAction(requestData));
  }, [dispatch, requestData]);

  // Sync state to URL whenever it changes
  useEffect(() => {
    const params = new URLSearchParams();

    // Pagination
    if (pagination.pageIndex > 0) {
      params.set("page", (pagination.pageIndex + 1).toString());
    }
    if (pagination.pageSize !== 10) {
      params.set("pageSize", pagination.pageSize.toString());
    }

    // Sorting
    if (sorting.length > 0) {
      params.set("sortBy", sorting[0].id);
      params.set("sortOrder", sorting[0].desc ? "DESC" : "ASC");
    }

    // Filters
    if (filters.companyName) params.set("companyName", filters.companyName);
    if (filters.intacctId) params.set("intacctId", filters.intacctId);
    if (filters.salesforceId) params.set("salesforceId", filters.salesforceId);
    if (filters.stage) params.set("stage", filters.stage);

    setSearchParams(params, { replace: true });
  }, [pagination, sorting, filters, setSearchParams]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (filterKey: string, value: string | undefined) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterKey]: value,
    }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return {
    companies,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    columnVisibility,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
};
